import React, { useEffect, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";

// Infinite-scroll replacement for LoadMoreButton: renders an invisible
// sentinel at the bottom of the list and calls onLoadMore itself once it
// scrolls into view, instead of waiting for a tap. Used for product listings
// specifically (Home.js, and the "Add Items" picker in LiveOrders.js) —
// order lists (OnlineOrders/CompletedOrders/LiveOrders' own order list) keep
// the explicit button, since paging through those is a deliberate action.
export default function AutoLoadMore({ hasMore, loadingMore, onLoadMore }) {
  const sentinelRef = useRef(null);
  // Mirrored into a ref so the observer callback always sees the latest
  // value without having to tear down and resubscribe on every toggle.
  const loadingRef = useRef(loadingMore);
  loadingRef.current = loadingMore;

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // loadingRef guards against firing a second load while one is
        // already in flight — the sentinel can stay intersecting across a
        // re-render before `loadingMore` flips back to true.
        if (entries[0].isIntersecting && !loadingRef.current) onLoadMore();
      },
      { rootMargin: "200px" } // start loading a bit before it's actually on-screen
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (!hasMore) return null;
  return (
    <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 2, minHeight: 40 }}>
      {loadingMore && <CircularProgress size={20} />}
    </Box>
  );
}
